/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import { AttachState } from "@fluidframework/container-definitions";
import { assert } from "@fluidframework/core-utils/internal";
import { getSnapshotTreeAndBlobsFromSerializedContainer } from "./utils.js";
/**
 * Executes the attach process state machine based on the provided data and services.
 * This method is retriable on failure. Based on the provided initialAttachmentData
 * this method will resume the attachment process and attempt to complete it.
 *
 * @param AttachProcessProps - The data and services necessary to run the attachment process
 * @returns - The attach summary (only if offline load is enabled), or undefined
 */
export const runRetriableAttachProcess = async ({ detachedBlobStorage, createOrGetStorageService, setAttachmentData, createAttachmentSummary, offlineLoadEnabled, initialAttachmentData, }) => {
    let currentData = initialAttachmentData;
    if (currentData.blobs === undefined) {
        // If attachment blobs were uploaded in detached state we will go through a different attach flow
        const outstandingAttachmentBlobs = detachedBlobStorage !== undefined && detachedBlobStorage.size > 0;
        // Determine the next phase of attaching which depends on if there are attachment blobs
        // if there are, we will stay detached, so an empty file can be created, and the blobs
        // uploaded, otherwise we will get the summary to create the file with and move to attaching
        currentData = outstandingAttachmentBlobs
            ? {
                state: AttachState.Detached,
                blobs: "outstanding",
                redirectTable: new Map(),
            }
            : {
                state: AttachState.Attaching,
                summary: createAttachmentSummary(),
                blobs: "none",
            };
        setAttachmentData(currentData);
    }
    // this has to run here, as it is what creates the file
    // and we need to file for all possible cases after this point
    const storage = await createOrGetStorageService(currentData.summary);
    if (currentData.blobs === "outstanding") {
        const { redirectTable } = currentData;
        // upload blobs to storage
        assert(!!detachedBlobStorage, 0x24e /* "assertion for type narrowing" */);
        // build a table mapping IDs assigned locally to IDs assigned by storage and pass it to runtime to
        // support blob handles that only know about the local IDs
        while (redirectTable.size < detachedBlobStorage.size) {
            const newIds = detachedBlobStorage.getBlobIds().filter((id) => !redirectTable.has(id));
            for (const id of newIds) {
                const blob = await detachedBlobStorage.readBlob(id);
                const response = await storage.createBlob(blob);
                redirectTable.set(id, response.id);
            }
        }
        setAttachmentData((currentData = {
            state: AttachState.Attaching,
            summary: createAttachmentSummary(redirectTable),
            blobs: "done",
        }));
    }
    assert(currentData.state === AttachState.Attaching, 0x8e2 /* must be attaching by this point */);
    if (currentData.blobs === "done") {
        // done means outstanding blobs were uploaded.
        // in that case an empty file was created, the blobs were uploaded
        // and now this finally uploads the summary
        await storage.uploadSummaryWithContext(currentData.summary, {
            referenceSequenceNumber: 0,
            ackHandle: undefined,
            proposalHandle: undefined,
        });
    }
    const snapshot = offlineLoadEnabled
        ? getSnapshotTreeAndBlobsFromSerializedContainer(currentData.summary)
        : undefined;
    setAttachmentData((currentData = {
        state: AttachState.Attached,
    }));
    return snapshot;
};
//# sourceMappingURL=attachment.js.map